import {
    Show,
    SimpleShowLayout,
    TextField,
    useRecordContext,
    TopToolbar,
    FunctionField,
    Loading,
    useRedirect,
    ReferenceField,
} from "react-admin";
import { Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ChannelChart, SeriesKey } from './ChannelChart';
import { EMPTY } from './plotting';
import { IntegralResultsTable } from './IntegralResults';
import { ChannelWorkflowNav } from './WorkflowNav';

const InstrumentChannelShowActions = () => {
    const redirect = useRedirect();
    const record = useRecordContext();

    if (!record || !record.id) {
        return null;
    };

    return (
        <TopToolbar>
            <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => redirect('show', 'instruments', record.experiment_id)}
            >
                Experiment
            </Button>
        </TopToolbar>
    );
}

const LinePlotShow = () => {
    const record = useRecordContext();
    if (!record) {
        return <Loading />;
    }

    const corrected = record.baseline_values ?? EMPTY;
    const hasBaseline = corrected.length > 0;

    // Once corrected, the raw signal and the spline that produced it become
    // reference material: kept off the plot until enabled from the legend
    const hidden: SeriesKey[] = hasBaseline ? ['raw', 'spline', 'picked'] : [];

    return (
        <ChannelChart
            title={record.channel_name}
            time={record.time_values ?? EMPTY}
            raw={record.raw_values ?? EMPTY}
            spline={record.baseline_spline}
            corrected={corrected}
            picked={record.baseline_chosen_points}
            pickedLabel="Baseline points"
            bands={record.integral_chosen_pairs}
            hidden={hidden}
        />
    );
}

const ShowWorkflowNav = () => {
    const record = useRecordContext();
    const hasBaseline = (record?.baseline_values ?? []).length > 0;

    return (
        <ChannelWorkflowNav
            step={0}
            hint={hasBaseline
                ? 'Showing the baseline-corrected signal with the integrated area per sample. Raw data and the baseline spline are hidden; enable them from the legend. Export CSVs from the experiment page.'
                : 'Pick baseline points to correct this channel, then integrate a range per sample.'}
        />
    );
};

export const InstrumentChannelShow = () => (
    <Show actions={<InstrumentChannelShowActions />}>
        <SimpleShowLayout >
            <ShowWorkflowNav />
            <ReferenceField
                label="Experiment"
                source="experiment_id"
                reference="instruments"
                link="show"
            >
                <TextField source="name" />
            </ReferenceField>
            <TextField source="channel_name" />
            <FunctionField label="Number of values" render={record => (record.raw_values ?? []).length} />
            <LinePlotShow />
            <Typography variant="h6">Integral results</Typography>
            <IntegralResultsTable />
        </SimpleShowLayout>
    </Show >
);

export default InstrumentChannelShow;
